import type { RouteLocationNormalized } from 'vue-router'
import type { Composer } from 'vue-i18n'

export interface BreadcrumbItem {
  title: string
  to?: string | { name: string }
  disabled?: boolean
  icon?: string
}

/**
 * Route configuration for breadcrumb generation
 */
const routeBreadcrumbConfig: Record<string, { 
  label: string
  icon?: string
  parent?: string
}> = {
  'dashboard': {
    label: 'common.home',
    icon: 'mdi-home'
  },
  'login': {
    label: 'auth.login',
    icon: 'mdi-login'
  },
  'register': {
    label: 'register.title',
    icon: 'mdi-account-plus',
    parent: 'dashboard'
  },
  'profile': {
    label: 'profile.title',
    icon: 'mdi-account',
    parent: 'dashboard'
  },
  'users': {
    label: 'users.title',
    icon: 'mdi-account-group',
    parent: 'dashboard'
  },
  'admin-config': {
    label: 'admin.config.title',
    icon: 'mdi-cog',
    parent: 'dashboard'
  },
  'setup': {
    label: 'setup.title',
    icon: 'mdi-cog'
  },
  'email-verification': {
    label: 'emailVerification.title',
    icon: 'mdi-email-check',
    parent: 'dashboard'
  },
  'email-verification-form': {
    label: 'emailVerification.title',
    icon: 'mdi-email-check',
    parent: 'dashboard'
  },
  'email-change-verification': {
    label: 'emailVerification.title',
    icon: 'mdi-email-check',
    parent: 'profile'
  },
  'not-found': {
    label: 'errors.notFound.title',
    icon: 'mdi-alert-circle',
    parent: 'dashboard'
  }
}

/**
 * Generates breadcrumbs for the current route
 */
export function generateBreadcrumbs(
  route: RouteLocationNormalized,
  t: Composer['t']
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = []
  const routeName = route.name as string

  if (!routeName || !routeBreadcrumbConfig[routeName]) {
    return breadcrumbs
  }

  // Build breadcrumb chain by following parent relationships
  const buildChain = (name: string): string[] => {
    const config = routeBreadcrumbConfig[name]
    if (!config) return []
    
    const chain = [name]
    if (config.parent && config.parent !== name) {
      chain.unshift(...buildChain(config.parent))
    }
    return chain
  }

  const chain = buildChain(routeName)

  // Convert chain to breadcrumb items
  chain.forEach((name, index) => {
    const config = routeBreadcrumbConfig[name]
    const isLast = index === chain.length - 1

    breadcrumbs.push({
      title: t(config.label),
      to: isLast ? undefined : { name },
      disabled: isLast,
      icon: config.icon
    })
  })

  return breadcrumbs
}

/**
 * Gets the page title for the current route
 */
export function getPageTitle(
  route: RouteLocationNormalized,
  t: Composer['t']
): string {
  const routeName = route.name as string
  const config = routeBreadcrumbConfig[routeName]
  
  if (!config) {
    return t('app.title')
  }
  
  return t(config.label)
}
